import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  adminCatalogApi,
  adminCatalogKey,
  formatMinorForInput,
  parseMoneyToMinor,
} from '@/lib/admin-catalog-api';
import { getErrorMessage } from '@/lib/errors';
import { FeatureEditor } from '@/modules/admin/catalog/FeatureEditor';
import {
  fromFeatureRows,
  incompleteRows,
  toFeatureRows,
  type FeatureRow,
} from '@/modules/admin/catalog/feature-limits';
import type { BillingInterval, Plan } from '@/types/api';

/**
 * Create or edit a plan.
 *
 * ## The two fields that decide whether the platform works
 *
 * **Entitlements**, and specifically `servers`. Without it a subscribed customer sees 0 / 0
 * servers and can add none — the platform is inert for them, with nothing on screen saying
 * why. The editor warns before saving a plan without it.
 *
 * **Price**, which the API takes in minor units. Nobody types 2900 meaning £29, so this takes
 * "29", "29.00" or "£29" and converts. A price that cannot be parsed blocks the save rather
 * than defaulting — 0 is a legitimate price for a free plan, so falling back to it would
 * quietly publish a paid plan for nothing.
 */
export function PlanDialog({
  productId,
  productName,
  plan,
  onClose,
}: {
  productId: string;
  productName: string;
  /** Null to create. */
  plan: Plan | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  const [name, setName] = useState(plan?.name ?? '');
  const [description, setDescription] = useState(plan?.description ?? '');
  const [interval, setInterval] = useState<BillingInterval>(plan?.billing_interval ?? 'monthly');
  const [price, setPrice] = useState(plan === null ? '' : formatMinorForInput(plan.price_minor));
  const [currency, setCurrency] = useState(plan?.currency ?? 'GBP');
  const [trialDays, setTrialDays] = useState(String(plan?.trial_days ?? 0));
  const [features, setFeatures] = useState<FeatureRow[]>(
    plan === null
      ? [{ key: 'servers', kind: 'quota', quota: '10', enabled: true }]
      : toFeatureRows(plan.features),
  );

  // Price, currency and interval are frozen once a plan is live — the aggregate refuses
  // to reprice an active plan so existing subscribers keep the terms they were sold on. The
  // fields are disabled rather than left to fail: a 422 after filling in a form teaches
  // nothing, whereas a locked field with a reason teaches the rule.
  const isLive = plan?.status === 'active';

  const priceMinor = parseMoneyToMinor(price);
  const trial = Number.parseInt(trialDays, 10);
  // Every adapter the platform registers. A plan needs an id for whichever one you actually sell
  // through; the rest stay blank and the checkout refuses those gateways for this plan.
  const [priceIds, setPriceIds] = useState<Record<string, string>>({
    paddle: plan?.gateway_price_ids?.paddle ?? '',
    stripe: plan?.gateway_price_ids?.stripe ?? '',
    lemon_squeezy: plan?.gateway_price_ids?.lemon_squeezy ?? '',
    '2checkout': plan?.gateway_price_ids?.['2checkout'] ?? '',
  });

  const unfinished = incompleteRows(features);
  const built = fromFeatureRows(features);
  const missingServers = !('servers' in built);

  const problem = (() => {
    if (name.trim() === '') return 'Give the plan a name.';
    if (priceMinor === null) return 'That price cannot be read. Use a number like 29 or 29.00.';
    if (currency.trim().length !== 3) return 'Currency must be a three-letter code, e.g. GBP.';
    if (!Number.isFinite(trial) || trial < 0) return 'Trial days must be 0 or more.';
    if (unfinished.length > 0) {
      return `These entitlements have no limit set: ${unfinished.join(', ')}.`;
    }

    return null;
  })();

  const save = useMutation({
    mutationFn: () => {
      const input = {
        product_id: productId,
        name: name.trim(),
        description: description.trim(),
        billing_interval: interval,
        price_minor: priceMinor ?? 0,
        currency: currency.trim().toUpperCase(),
        trial_days: trial,
        features: built,
        // Blank fields are sent and dropped server-side, which is how an operator removes one.
        gateway_price_ids: priceIds,
      };

      return plan === null
        ? adminCatalogApi.createPlan(input)
        : adminCatalogApi.updatePlan(plan.id, input);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: adminCatalogKey });
      onClose();
    },
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{plan === null ? 'New plan' : 'Edit plan'}</DialogTitle>
          <DialogDescription>
            Under <strong>{productName}</strong>. Subscriptions are sold against a plan, and only
            an active one.
          </DialogDescription>
        </DialogHeader>

        {save.isError && <Alert variant="danger">{getErrorMessage(save.error)}</Alert>}

        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="plan-name">Name</Label>
            <Input
              id="plan-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={150}
            />
          </div>

          {isLive && (
            <Alert variant="warning">
              This plan is live, so its price, currency and billing interval are locked.
              Existing subscribers keep the terms they were sold on. To change them, archive this
              plan and publish a replacement. Everything else below can still be edited.
            </Alert>
          )}

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="plan-price">Price</Label>
              <Input
                id="plan-price"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                inputMode="decimal"
                disabled={isLive}
              />
              {price.trim() !== '' && priceMinor !== null && (
                <p className="text-xs text-muted-foreground">
                  Stored as {priceMinor} minor units
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="plan-currency">Currency</Label>
              <Input
                id="plan-currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                maxLength={3}
                className="font-mono"
                disabled={isLive}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="plan-interval">Billing</Label>
              <Select
                id="plan-interval"
                value={interval}
                onChange={(e) => setInterval(e.target.value as BillingInterval)}
                disabled={isLive}
              >
                <option value="monthly">monthly</option>
                <option value="yearly">yearly</option>
                <option value="lifetime">lifetime</option>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="plan-trial">Trial days</Label>
              <Input
                id="plan-trial"
                value={trialDays}
                onChange={(e) => setTrialDays(e.target.value)}
                inputMode="numeric"
              />
            </div>
          </div>

          <FeatureEditor rows={features} onChange={setFeatures} />

          <GatewayPriceEditor ids={priceIds} onChange={setPriceIds} />

          {missingServers && (
            <Alert variant="warning">
              This plan grants no <code className="font-mono">servers</code> entitlement.
              Customers on it will see <strong>0 / 0 servers</strong> and will not be able to add
              any. Deliberate for an add-on plan; almost certainly a mistake otherwise.
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="plan-description">
              Description <span className="font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              id="plan-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={5000}
            />
          </div>

          {problem !== null && <Alert variant="danger">{problem}</Alert>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={save.isPending || problem !== null}
          >
            {save.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
            {plan === null ? 'Create plan' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** How each gateway names this plan on its own side. */
const GATEWAYS: Array<{ key: string; label: string; hint: string }> = [
  { key: 'paddle', label: 'Paddle', hint: 'The price ID, starting with pri_.' },
  { key: 'stripe', label: 'Stripe', hint: 'The price ID, starting with price_.' },
  { key: 'lemon_squeezy', label: 'Lemon Squeezy', hint: 'The numeric variant ID.' },
  { key: '2checkout', label: '2Checkout', hint: 'The product code.' },
];

/**
 * The price id each gateway knows this plan by.
 *
 * ## Why this field has to exist
 *
 * Every adapter used to take a single price id from the environment and apply it to every
 * checkout. That was coincidentally correct while the catalogue held one plan. With six, a customer
 * choosing Business yearly was sent to the gateway with whatever single price was configured — and
 * charged the wrong amount. The checkout opened, the payment succeeded, and nothing failed.
 *
 * A blank field means the plan is not sold through that gateway, and the checkout says so rather
 * than substituting anything. That is the difference between refusing to take money and taking the
 * wrong amount.
 */
function GatewayPriceEditor({
  ids,
  onChange,
}: {
  ids: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}) {
  const configured = GATEWAYS.filter((gateway) => (ids[gateway.key] ?? '').trim() !== '').length;

  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-medium">Payment gateway price IDs</p>
        <p className="text-muted-foreground mt-0.5 text-xs">
          Create this plan at your gateway first, then paste the id it gives you. A gateway left
          blank cannot sell this plan. Checkout refuses it rather than charging a different plan's
          price.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {GATEWAYS.map((gateway) => (
          <div key={gateway.key} className="space-y-1.5">
            <Label htmlFor={`price-${gateway.key}`}>{gateway.label}</Label>
            <Input
              id={`price-${gateway.key}`}
              value={ids[gateway.key] ?? ''}
              onChange={(e) => onChange({ ...ids, [gateway.key]: e.target.value })}
              className="font-mono text-xs"
            />
            <p className="text-xs text-muted-foreground">{gateway.hint}</p>
          </div>
        ))}
      </div>

      {configured === 0 && (
        <Alert variant="warning" className="text-xs">
          No gateway can sell this plan yet. It can still be assigned by hand from the subscriptions
          screen, but a customer reaching checkout will be told the plan is unavailable.
        </Alert>
      )}
    </div>
  );
}
